'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { languageSelectorVariants } from './LanguageSelector.styles';
import { switchLanguageByEntry } from '@/app/actions/language';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { trackAdobeClick } from '@/utils/adobe/click';
import { LanguageService } from '@/lib/services/language-service';
import { localizeHref } from '@/utils/localize-href';

const EN_LANG_CODE = 'en-us';
const ES_LANG_CODE = 'es';
const EN_LABEL = 'English';
const ES_LABEL = 'Español';

type LanguageSelectorVariant = 'pill' | 'mobile';

interface LanguageSelectorProps {
  entryUid?: string;
  contentTypeUid?: string;
  hasAlternateLanguage?: boolean;
  variant?: LanguageSelectorVariant;
  englishUrl?: string;
  spanishUrl?: string;
}

export const LanguageSelector = ({
  entryUid,
  contentTypeUid = 'standard_content_page',
  hasAlternateLanguage = true,
  variant = 'pill',
  englishUrl,
  spanishUrl,
}: LanguageSelectorProps) => {
  const params = useParams();
  const router = useRouter();

  const currentLocale = params.locale as string;
  const { globalLabels } = useGlobalLabels();
  const isEnglish = currentLocale !== ES_LANG_CODE;
  const isDisabled = !hasAlternateLanguage;
  const targetLanguageLabel = isEnglish ? ES_LABEL : EN_LABEL;
  const UNAVAILABLE_TOOLTIP = globalLabels.alternate_language_not_available_label;

  const targetPath = isEnglish
    ? localizeHref(spanishUrl, ES_LANG_CODE)
    : localizeHref(englishUrl, EN_LANG_CODE);

  // Warm the route cache so the switch feels instant.
  useEffect(() => {
    if (targetPath) router.prefetch(targetPath);
  }, [targetPath, router]);

  const [mobileTooltipOpen, setMobileTooltipOpen] = useState(false);
  const mobileWrapperRef = useRef<HTMLSpanElement>(null);

  // Keep localStorage in sync with the active URL locale so client-side reads of
  // getStoredLanguagePreference() are accurate even on direct URL navigation.
  useEffect(() => {
    const activeLocale = currentLocale === ES_LANG_CODE ? ES_LANG_CODE : EN_LANG_CODE;
    LanguageService.saveLanguagePreference(activeLocale);
  }, [currentLocale]);

  // Closes the mobile tooltip when tapping outside the wrapper
  useEffect(() => {
    if (!mobileTooltipOpen) return;
    const handleOutsideTouch = (event: TouchEvent) => {
      if (!mobileWrapperRef.current?.contains(event.target as Node)) {
        setMobileTooltipOpen(false);
      }
    };
    document.addEventListener('touchstart', handleOutsideTouch);
    return () => document.removeEventListener('touchstart', handleOutsideTouch);
  }, [mobileTooltipOpen]);

  const handleToggle = useCallback(async () => {
    trackAdobeClick({
      clickName: `Switch to ${targetLanguageLabel}`,
      clickType: 'button',
      clickLocation: 'languageSelector',
    });
    if (isDisabled) return;
    const targetLangCode = isEnglish ? ES_LANG_CODE : EN_LANG_CODE;
    // Sync cookie + localStorage before navigating so the next render sees the
    // new preference (server action stays in sync via cookie when used).
    LanguageService.saveLanguagePreference(targetLangCode);

    // Fast path: pre-resolved URL → client-side nav (no server action, no CMS hop).
    if (targetPath) {
      router.push(targetPath);
      return;
    }

    // Fallback: target URL wasn't pre-resolved (no entryUid or fetch failed).
    if (!entryUid) return;
    await switchLanguageByEntry(entryUid, contentTypeUid, targetLangCode);
  }, [isEnglish, entryUid, contentTypeUid, isDisabled, targetLanguageLabel, targetPath, router]);

  // Toggles the mobile tooltip when disabled, otherwise switches language
  const handleMobileClick = useCallback(() => {
    if (isDisabled) {
      setMobileTooltipOpen((prev) => !prev);
      return;
    }
    handleToggle();
  }, [isDisabled, handleToggle]);

  const {
    base,
    labelWrapper,
    labelEn,
    labelEs,
    mobileWrapper,
    mobileButton,
    mobileIcon,
    mobileLabel,
    tooltipWrapper,
    tooltip,
  } = languageSelectorVariants({
    isEnglish,
    isDisabled,
    isMobile: variant === 'mobile',
    isEnglishDisabled: !Boolean(englishUrl),
    isSpanishDisabled: !Boolean(spanishUrl),
  });

  // WCAG 2.5.3 Label in Name: the accessible name must contain the visible label text
  const ariaLabel =
    variant === 'mobile'
      ? `Switch to ${targetLanguageLabel}`
      : `English ESPAÑOL — switch to ${targetLanguageLabel}`;

  if (variant === 'mobile') {
    return (
      <span ref={mobileWrapperRef} className={mobileWrapper()}>
        <button
          type="button"
          onClick={handleMobileClick}
          aria-disabled={isDisabled}
          aria-label={ariaLabel}
          aria-describedby={isDisabled && mobileTooltipOpen ? 'language-unavailable' : undefined}
          className={mobileButton()}
        >
          <i className={mobileIcon()} aria-hidden="true" />
          <span className={mobileLabel()}>{targetLanguageLabel}</span>
        </button>
        {isDisabled && mobileTooltipOpen && (
          <span id="language-unavailable" role="tooltip" className={tooltip()}>
            {UNAVAILABLE_TOOLTIP}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className={tooltipWrapper()}>
      <button
        type="button"
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-disabled={isDisabled}
        className={base()}
      >
        <div className={labelWrapper()}>
          <span className={labelEn()}>English</span>
          <span className={labelEs()}>ESPAÑOL</span>
        </div>
        {isDisabled && (
          <span role="tooltip" className={tooltip({ hoverable: true })}>
            {UNAVAILABLE_TOOLTIP}
          </span>
        )}
      </button>
    </span>
  );
};
